import { LOOKUP_CONFIG } from "@/config/lookups";

const setTitle = (title: string) => {
  return title
    ? `${title} | Demulla Accounting Portal`
    : "Demulla Accounting Portal";
};

const authRoutes = [
  {
    path: "/auth/login",
    name: "auth.login",
    meta: {
      title: setTitle("Login"),
    },
    component: () => import("@/views/auth/Login.vue"),
  },
  {
    path: "/auth/login-otp/:node",
    name: "auth.login-otp",
    meta: {
      title: setTitle("Login OTP"),
    },
    component: () => import("@/views/auth/LoginOtp.vue"),
  },
  {
    path: "/auth/authenticator/:node/:enabled",
    name: "auth.authenticator",
    meta: {
      title: setTitle("Authenticator OTP"),
    },
    component: () => import("@/views/auth/Authenticator.vue"),
  },
  {
    path: "/auth/reset-password",
    name: "auth.reset",
    meta: {
      title: setTitle("Reset Password"),
    },
    component: () => import("@/views/auth/ResetPass.vue"),
  },
];

const errorRoutes = [
  {
    path: "/auth/error-404",
    name: "error.404",
    meta: {
      title: setTitle("Error 404"),
    },
    component: () => import("@/views/auth/Error404.vue"),
  },
  {
    path: "/auth/error-500",
    name: "error.500",
    meta: {
      title: setTitle("Error 500"),
    },
    component: () => import("@/views/auth/Error500.vue"),
  },
  {
    path: "/auth/error-403",
    name: "error.403",
    meta: {
      title: setTitle("Error 403"),
    },
    component: () => import("@/views/auth/Error403.vue"),
  },
  {
    path: "/:catchAll(.*)",
    redirect: "/auth/error-404",
  },
];

const dashboardRoutes = [
  {
    path: "/",
    name: "dashboard.index",
    meta: {
      title: setTitle("Dashboard"),
      authRequired: true,
    },
    component: () => import("@/views/dashboard/DashboardPage.vue"),
  },
  {
    path: "/:id",
    name: "profile.view",
    meta: {
      title: setTitle("Dashboard"),
      authRequired: true,
    },
    component: () => import("@/views/dashboard/DashboardPage.vue"),
  },
  {
    path: "/system/settings/users/:id/setup-authenticator",
    name: "system.settings.user.view.setup",
    meta: {
      title: setTitle("Setup User Authenticator"),
      authRequired: true,
    },
    component: () =>
      import("@/views/system/settings/users/SetUpAuthenticator.vue"),
  },
];

const notificationRoutes = [
  {
    path: "/pages/notifications",
    name: "notifications.center",
    meta: {
      title: setTitle("Notifications"),
      authRequired: true,
    },
    component: () => import("@/views/notifications/NotificationsCenter.vue"),
  },
];

const complianceRoutes = [
  {
    path: "/compliance/breaches",
    name: "compliance.breaches",
    meta: {
      title: setTitle("Compliance Breaches"),
      authRequired: true,
    },
    component: () => import("@/views/compliance/ComplianceBreaches.vue"),
  },
  {
    path: "/compliance/breaches/create",
    name: "compliance.breach.create",
    meta: {
      title: setTitle("Log Compliance Breach"),
      authRequired: true,
    },
    component: () => import("@/views/compliance/CreateComplianceBreach.vue"),
  },
  {
    path: "/compliance/breaches/:id",
    name: "compliance.breach.detail",
    meta: {
      title: setTitle("Compliance Breach Detail"),
      authRequired: true,
    },
    component: () => import("@/views/compliance/ComplianceBreachDetail.vue"),
  },
  {
    path: "/compliance/breaches/:id/edit",
    name: "compliance.breach.edit",
    meta: {
      title: setTitle("Edit Compliance Breach"),
      authRequired: true,
    },
    component: () => import("@/views/compliance/CreateComplianceBreach.vue"),
  },
];

const RiskRoutes = [
  {
    path: "/risk/register",
    name: "risk.register",
    meta: {
      title: setTitle("Risk register"),
      authRequired: true,
    },
    component: () => import("@/views/risk-register/RiskRegister.vue"),
  },
  {
    path: "/risk/register/:id",
    name: "risk.register.view",
    meta: {
      title: setTitle("Risk register"),
      authRequired: true,
    },
    component: () => import("@/views/risk-register/ViewRisk.vue"),
  },
  {
    path: "/risk/register/create",
    name: "risk.register.create",
    meta: { authRequired: true },
    component: () => import("@/views/risk-register/AddRisk.vue"),
  },
  {
    path: "/risk/register/:id/edit",
    name: "risk.register.edit",
    meta: { authRequired: true },
    component: () => import("@/views/risk-register/AddRisk.vue"),
  },
  {
    path: "/risk/monitoring",
    name: "risk.monitoring",
    meta: { authRequired: true },
    component: () => import("@/views/risk-register/RiskAppetitePanel.vue"),
  },
];

const incidentRoutes = [
  {
    path: "/incident/listing",
    name: "incident.listing",
    meta: {
      title: setTitle("Incident Listing"),
      authRequired: true,
    },
    component: () => import("@/views/incident/IncidentDashboard.vue"),
  },
  {
    path: "/incident/report/:id?",
    name: "incident.report",
    meta: {
      title: setTitle("Report Incident"),
      authRequired: true,
    },
    component: () => import("@/views/incident/ReportIncident.vue"),
  },

  {
    path: "/incident/:id",
    name: "incident.detail",
    meta: {
      title: setTitle("Incident Detail"),
      authRequired: true,
    },
    component: () => import("@/views/incident/IncidentDetail.vue"),
  },
];

const fraudRoutes = [
  {
    path: "/fraud-management/list",
    name: "fraud-management.list",
    meta: {
      title: setTitle("Fraud Management"),
      authRequired: true,
    },
    component: () => import("@/views/fraud-management/FraudList.vue"),
  },
  {
    path: "/fraud-management/risks",
    name: "fraud-management.risks",
    meta: {
      title: setTitle("Fraud Risks"),
      authRequired: true,
    },
    component: () => import("@/views/fraud-management/FraudRiskList.vue"),
  },
  {
    path: "/fraud-management/submit",
    name: "fraud-management.submit",
    meta: {
      title: setTitle("Submit Fraud Case"),
      authRequired: true,
    },
    component: () => import("@/views/fraud-management/FraudSubmissionForm.vue"),
  },
  {
    path: "/fraud-management/edit/:id",
    name: "fraud-management.edit",
    meta: {
      title: setTitle("Edit Fraud Case"),
      authRequired: true,
    },
    component: () => import("@/views/fraud-management/FraudSubmissionForm.vue"),
  },
  {
    path: "/fraud-management/:id",
    name: "fraud-management.view",
    meta: {
      title: setTitle("View Fraud Case"),
      authRequired: true,
    },
    component: () => import("@/views/fraud-management/FraudDetailView.vue"),
  },
];
const executiveDashboardRoutes = [
  {
    path: "/executive/dashboard",
    name: "executive.dashboard",
    meta: {
      title: setTitle("Executive Dashbaord"),
      authRequired: true,
    },
    component: () => import("@/views/executive/Dashboard.vue"),
  },
];
const reportRoutes = [
  {
    path: "/reports/generated",
    name: "reports.generated",
    meta: {
      title: setTitle("Regulatory Reporting"),
      authRequired: true,
    },
    component: () => import("@/views/reports/GeneratedReports.vue"),
  },
  {
    path: "/reports/builder",
    name: "reports.builder",
    meta: {
      title: setTitle("Regulatory Reporting"),
      authRequired: true,
    },
    component: () => import("@/views/reports/ReportBuilder.vue"),
  },
  {
    path: "/reports/:id",
    name: "reports.view",
    meta: {
      title: setTitle("Report"),
      authRequired: true,
    },
    component: () => import("@/views/reports/ReportView.vue"),
  },
];

const escalationManagementRoutes = [
  {
    path: "/escalations",
    name: "escalation.list",
    meta: {
      title: setTitle("Escalations Management"),
      authRequired: true,
    },
    component: () => import("@/views/escalations/EscalationManagement.vue"),
  },
];
const AuditPlanRoutes = [
  {
    path: "/audit/plans",
    name: "audit.plans",
    meta: {
      title: setTitle("Audit plans"),
      authRequired: true,
    },
    component: () => import("@/views/audit/plans/AuditPlansList.vue"),
  },
  {
    path: "/audit/plans/create",
    name: "audit.plans.create",
    meta: {
      title: setTitle("Audit plans"),
      authRequired: true,
    },
    component: () => import("@/views/audit/plans/AddAuditPlan.vue"),
  },
  {
    path: "/audit/plans/:id/edit",
    name: "audit.plans.edit",
    meta: {
      title: setTitle("Audit plans"),
      authRequired: true,
    },
    component: () => import("@/views/audit/plans/AddAuditPlan.vue"),
  },
  {
    path: "/audit/plans/:id/view",
    name: "audit.plans.view",
    meta: {
      title: setTitle("Audit plans"),
      authRequired: true,
    },
    component: () => import("@/views/audit/plans/ViewAuditPlan.vue"),
  },
  {
    path: "/audit/findings",
    name: "audit.findings",
    meta: {
      title: setTitle("Audit Findings"),
      authRequired: true,
    },
    component: () => import("@/views/audit/plans/AuditFindingsList.vue"),
  },
  {
    path: "/audit/findings/:id",
    name: "audit.findings.view",
    meta: {
      title: setTitle("Audit Findings"),
      authRequired: true,
    },
    component: () => import("@/views/audit/plans/FindingDetailPage.vue"),
  },
];

const systemRoutes = [
  {
    path: "/system/audit-trail",
    name: "system.audit-trail",
    meta: {
      title: setTitle("Audit Trail"),
      authRequired: true,
    },
    component: () => import("@/views/system/audit-trail/AuditTrail.vue"),
  },
  {
    path: "/system/audit-trail/:id",
    name: "audit-trail.view",
    meta: {
      title: setTitle("Audit Trail"),
      authRequired: true,
    },
    component: () =>
      import("@/views/system/audit-trail/AuditTrailDetailView.vue"),
  },
  {
    path: "/system/users",
    name: "system.users",
    meta: {
      title: setTitle("System Users"),
      authRequired: true,
    },
    component: () =>
      import("@/views/system/users/SystemUsers.vue"),
  },
  {
    path: "/system/users/:id/view",
    name: "system.user.view",
    meta: {
      title: setTitle("System Users"),
      authRequired: true,
    },
    component: () =>
      import("@/views/system/users/ViewSystemUsers.vue"),
  },
  {
    path: "/system/roles",
    name: "system.roles",
    meta: {
      title: setTitle("Roles & Permissions"),
      authRequired: true,
    },
    component: () =>
      import("@/views/system/roles/RolesPermissions.vue"),
  },
];

// One explicit, individually-named route per lookup type — each gets its own
// sidebar menu item and highlights independently, instead of every lookup
// sharing a single ":type" route (which would make every sidebar link light
// up together, since the menu's active-state check matches by route name).
const referenceDataRoutes = Object.entries(LOOKUP_CONFIG).map(([type, config]) => ({
  path: `/system/reference-data/${type}`,
  name: `reference-data.${type}`,
  meta: {
    title: setTitle(config.label),
    authRequired: true,
    lookupType: type,
  },
  component: () => import("@/views/system/lookups/LookupManager.vue"),
}));

const CapaRoutes = [
  {
    path: "/capa-management/list",
    name: "capa.list",
    meta: {
      title: setTitle("CAPA Management"),
      authRequired: true,
    },
    component: () => import("@/views/capa-management/CapaListView.vue"),
  },
  {
    path: "/capa-management/create",
    name: "capa.create",
    meta: {
      title: setTitle("Create CAPA"),
      authRequired: true,
    },
    component: () => import("@/views/capa-management/CapaForm.vue"),
  },
  {
    path: "/capa-management/edit/:id",
    name: "capa.edit",
    meta: {
      title: setTitle("Edit CAPA"),
      authRequired: true,
    },
    component: () => import("@/views/capa-management/CapaForm.vue"),
  },
  {
    path: "/capa-management/dashboard",
    name: "capa.dashboard",
    meta: {
      title: setTitle("CAPA Dashboard Analytics"),
      authRequired: true,
    },
    component: () => import("@/views/capa-management/CapaDashboard.vue"),
  },
  {
    path: "/capa-management/:id",
    name: "capa.view",
    meta: {
      title: setTitle("View CAPA"),
      authRequired: true,
    },
    component: () => import("@/views/capa-management/CapaDetailView.vue"),
  },
  {
    path: "/capa-management/:id/root-cause-analysis",
    name: "capa.root-cause-analysis",
    meta: {
      title: setTitle("Root Cause Analysis"),
      authRequired: true,
    },
    component: () =>
      import("@/views/capa-management/CapaRootCauseAnalysis.vue"),
  },
  {
    path: "/capa-management/:id/approval",
    name: "capa.approval",
    meta: {
      title: setTitle("CAPA Approval"),
      authRequired: true,
    },
    component: () => import("@/views/capa-management/CapaApproval.vue"),
  },
  {
    path: "/capa-management/:id/documents",
    name: "capa.documents",
    meta: {
      title: setTitle("CAPA Documents"),
      authRequired: true,
    },
    component: () => import("@/views/capa-management/CapaDocumentCenter.vue"),
  },
];

const documentRoutes = [
  {
    path: "/documents",
    name: "documents.list",
    meta: {
      title: setTitle("Documents"),
      authRequired: true,
    },
    component: () => import("@/views/documents/DocumentList.vue"),
  },
  {
    path: "/documents/create",
    name: "documents.create",
    meta: {
      title: setTitle("Create Document"),
      authRequired: true,
    },
    component: () => import("@/views/documents/DocumentFormView.vue"),
  },
  {
    path: "/documents/:id",
    name: "documents.view",
    meta: {
      title: setTitle("View Document"),
      authRequired: true,
    },
    component: () => import("@/views/documents/DocumentView.vue"),
  },
  {
    path: "/documents/:id/edit",
    name: "documents.edit",
    meta: {
      title: setTitle("Edit Document"),
      authRequired: true,
    },
    component: () => import("@/views/documents/DocumentFormView.vue"),
  },
];
const policySopRoutes = [
  {
    path: "/policies",
    name: "policy.list",
    component: () => import("@/views/policies/PoliciesList.vue"),
    meta: { authRequired: true, title: "Policy Register" },
  },
  {
    path: "/policies/create",
    name: "policy.create",
    component: () => import("@/views/policies/AddPolicy.vue"),
    meta: { authRequired: true, title: "Draft Policy" },
  },
  {
    path: "/policies/:id/edit",
    name: "policy.edit",
    component: () => import("@/views/policies/AddPolicy.vue"),
    meta: { authRequired: true, title: "Edit Policy" },
  },
  {
    path: "/policies/:id/view",
    name: "policy.view",
    component: () => import("@/views/policies/PolicyDetail.vue"),
    meta: { authRequired: true, title: "View Policy" },
  },
  {
    path: "/policies/:id/acknowledge",
    name: "policy.acknowledge",
    component: () => import("@/views/policies/AcknowledgePolicy.vue"),
    meta: { authRequired: true, title: "Acknowledge Policy" },
  },
  {
    path: "/sops",
    name: "sop.list",
    component: () => import("@/views/sops/SopList.vue"),
    meta: { authRequired: true, title: "SOP Register" },
  },
  {
    path: "/sops/create",
    name: "sop.create",
    component: () => import("@/views/sops/AddSop.vue"),
    meta: { authRequired: true, title: "Draft SOP" },
  },
  {
    path: "/sops/:id/edit",
    name: "sop.edit",
    component: () => import("@/views/sops/AddSop.vue"),
    meta: { authRequired: true, title: "Edit SOP" },
  },
  {
    path: "/sops/:id/view",
    name: "sop.view",
    component: () => import("@/views/sops/SopDetail.vue"),
    meta: { authRequired: true, title: "View SOP" },
  },
  {
    path: "/sops/:id/acknowledge",
    name: "sop.acknowledge",
    component: () => import("@/views/sops/AcknowledgeSop.vue"),
    meta: { authRequired: true, title: "Acknowledge SOP" },
  },
  {
    path: "/policies-sops/acknowledgments",
    name: "policy.sop.acknowledgments",
    component: () =>
      import("@/views/acknowledgement/AcknowledgementDashboard.vue"),
    meta: { authRequired: true, title: "Acknowledgments dashboard" },
  },
];
export const allRoute = [
  ...authRoutes,
  ...errorRoutes,
  ...dashboardRoutes,
  ...complianceRoutes,
  ...RiskRoutes,
  ...incidentRoutes,
  ...fraudRoutes,
  ...CapaRoutes,
  ...executiveDashboardRoutes,
  ...systemRoutes,
  ...referenceDataRoutes,
  ...reportRoutes,
  ...escalationManagementRoutes,
  ...AuditPlanRoutes,
  ...documentRoutes,
  ...policySopRoutes,
  ...notificationRoutes,
];
